/**
 * SolutionFamilyDetailModal — the Solution Family detail overlay (DEV-411).
 *
 * Modal-over-overlay rendering of a single SolutionFamily: title + accept
 * toggle, technology/product counts, age/risk, description, pros/cons, risk
 * rationale, technology chips, a per-family deep-research action, and the found
 * (representative) vendors. Composes the foundation tokens + primitives; the
 * page owns the data + mutation handlers and passes them in.
 */
import { ArrowUpRight, Loader2, Telescope, X } from 'lucide-react'

import { cn } from '../../lib/utils'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import Modal from '../../components/ui/Modal'
import Switch from '../../components/ui/Switch'

export interface RepVendor {
  name?: string
  product?: string
  url?: string
  rationale?: string
}

export type FamilyStatus = 'proposed' | 'accepted' | 'rejected' | 'superseded'

export interface SolutionFamily {
  id: string
  name: string
  description: string
  pros: string[]
  cons: string[]
  maturity: 'nascent' | 'emerging' | 'established' | 'mature'
  risk_level: 'low' | 'medium' | 'high' | 'very_high'
  risk_rationale: string
  match_score: number | null
  source: 'fast' | 'deep' | 'manual'
  status: FamilyStatus
  technology_id: number | null
  technology_name: string | null
  technology_path: string | null
  representative_vendors: RepVendor[]
  products_count: number
}

export interface FamilyResearchTriage {
  kicked_off: { technology_name: string }[]
  skipped_in_progress: { technology_name: string }[]
  skipped_fresh: { technology_name: string }[]
}

export const MATURITY_LABEL: Record<SolutionFamily['maturity'], string> = {
  nascent: 'Nascent',
  emerging: 'Emerging',
  established: 'Established',
  mature: 'Mature',
}

export const RISK_LABEL: Record<SolutionFamily['risk_level'], string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  very_high: 'Very High',
}

interface TechRef {
  id: number | null
  name: string
}

/**
 * The technologies a family covers. The backend anchors each family on a single
 * technology (its descendants define the vendor pool), so today this is the
 * anchor only. See the note in ProgramSolutions about the multi-technology gap.
 */
export const familyTechnologies = (family: SolutionFamily): TechRef[] =>
  family.technology_name
    ? [{ id: family.technology_id, name: family.technology_name }]
    : []

/**
 * Technology + product counts. Shared verbatim by the grid card and this
 * detail header so the two never drift.
 */
export const FamilyStats = ({ family }: { family: SolutionFamily }) => {
  const techs = familyTechnologies(family)
  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-1">
        <span className="text-base text-foreground">{techs.length}</span>
        <span className="text-sm text-muted-foreground">
          {techs.length === 1 ? 'Technology' : 'Technologies'}
        </span>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-base text-foreground">{family.products_count}</span>
        <span className="text-sm text-muted-foreground">Products</span>
      </div>
    </div>
  )
}

/** Age / risk summary line. Pass a text-size class for card vs modal sizing. */
export const FamilyAgeRisk = ({
  family,
  className,
}: {
  family: SolutionFamily
  className?: string
}) => (
  <div className={cn('flex items-center gap-[18px] text-foreground', className)}>
    <span>Age: {MATURITY_LABEL[family.maturity] ?? family.maturity}</span>
    <span>Risk: {RISK_LABEL[family.risk_level] ?? family.risk_level}</span>
  </div>
)

/**
 * Vendor URLs come from LLM/deep-research output, so guard against
 * `javascript:`/`data:` and other non-web schemes before rendering as an href.
 * No base is passed, so relative / protocol-relative URLs throw and are
 * rejected (these are meant to be external absolute links).
 */
const safeHttpUrl = (raw?: string): string | null => {
  if (!raw) return null
  try {
    const u = new URL(raw)
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : null
  } catch {
    return null
  }
}

interface SolutionFamilyDetailModalProps {
  family: SolutionFamily | null
  isOpen: boolean
  onClose: () => void
  onToggleAccept: (family: SolutionFamily, next: boolean) => void
  onRunResearch: (family: SolutionFamily) => void
  toggling: boolean
  researching: boolean
  researchResult?: FamilyResearchTriage
}

const SolutionFamilyDetailModal = ({
  family,
  isOpen,
  onClose,
  onToggleAccept,
  onRunResearch,
  toggling,
  researching,
  researchResult,
}: SolutionFamilyDetailModalProps) => {
  if (!family) return null

  const accepted = family.status === 'accepted'
  const techs = familyTechnologies(family)
  const vendors = family.representative_vendors ?? []

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="max-w-[764px] max-h-[90vh] overflow-y-auto rounded-lg border border-border"
      role="dialog"
      aria-modal="true"
      aria-label={family.name}
    >
      <div className="flex flex-col gap-6">
        {/* Header: title + accept toggle + stats, with close button */}
        <div className="flex items-start gap-6 w-full">
          <div className="flex-1 min-w-0 flex flex-col gap-4">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-base font-medium text-foreground break-words">
                {family.name}
              </h2>
              <Switch
                checked={accepted}
                disabled={toggling}
                onCheckedChange={(next) => onToggleAccept(family, next)}
                aria-label={`Activate ${family.name} for this program`}
              />
            </div>
            <FamilyStats family={family} />
          </div>
          <Button
            variant="secondary"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Age / Risk + description */}
        <div className="flex flex-col gap-4 w-full">
          <FamilyAgeRisk family={family} className="text-sm" />
          {family.description && (
            <p className="text-xs leading-normal text-muted-foreground whitespace-pre-wrap">
              {family.description}
            </p>
          )}
        </div>

        {/* Pros / Cons */}
        {(family.pros?.length > 0 || family.cons?.length > 0) && (
          <div className="flex gap-6 w-full">
            {family.pros?.length > 0 && (
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                <p className="text-sm text-foreground">Pros</p>
                <ul className="list-disc ps-[18px] space-y-1 text-xs text-muted-foreground">
                  {family.pros.map((p, i) => (
                    <li key={i}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {family.cons?.length > 0 && (
              <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                <p className="text-sm text-foreground">Cons</p>
                <ul className="list-disc ps-[18px] space-y-1 text-xs text-muted-foreground">
                  {family.cons.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Risk rationale */}
        {family.risk_rationale && (
          <div className="flex flex-col gap-1.5 w-full">
            <p className="text-sm text-foreground">Risk rationale</p>
            <p className="text-xs leading-normal text-muted-foreground whitespace-pre-wrap">
              {family.risk_rationale}
            </p>
          </div>
        )}

        {/* Technologies + per-family deep research */}
        <div className="flex flex-col gap-4 w-full sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1.5 min-w-0">
            <p className="text-sm text-foreground">Technologies</p>
            {techs.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {techs.map((t, i) => (
                  <Badge key={t.id ?? i} title={family.technology_path ?? undefined}>
                    {t.name}
                  </Badge>
                ))}
              </div>
            ) : (
              <span className="text-xs text-muted-foreground">No anchor technology</span>
            )}
          </div>
          <Button
            variant="secondary"
            onClick={() => onRunResearch(family)}
            disabled={researching || !family.technology_id}
            className="shrink-0"
          >
            {researching ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Telescope className="size-4" />
            )}
            Run deep research (on anything new)
          </Button>
        </div>

        {researchResult && (
          <div className="text-xs text-muted-foreground space-y-1 -mt-2">
            {researchResult.kicked_off.length > 0 && (
              <div>
                <span className="font-medium text-foreground">Queued:</span>{' '}
                {researchResult.kicked_off.map((e) => e.technology_name).join(', ')}
              </div>
            )}
            {researchResult.skipped_in_progress.length > 0 && (
              <div>
                <span className="font-medium text-foreground">Already running:</span>{' '}
                {researchResult.skipped_in_progress
                  .map((e) => e.technology_name)
                  .join(', ')}
              </div>
            )}
            {researchResult.skipped_fresh.length > 0 && (
              <div>
                <span className="font-medium text-foreground">Skipped (recent):</span>{' '}
                {researchResult.skipped_fresh.map((e) => e.technology_name).join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Found vendors */}
        {vendors.length > 0 && (
          <div className="flex flex-col gap-3 w-full">
            <p className="text-sm text-foreground">Found Vendors</p>
            <div className="flex flex-col gap-4">
              {vendors.map((v, i) => {
                const detail = [v.product, v.rationale].filter(Boolean).join(' · ')
                const href = safeHttpUrl(v.url)
                return (
                  <div key={i} className="flex flex-col gap-1.5 w-full">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-foreground">
                        {v.name}
                      </span>
                      {href && (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                          aria-label={`Open ${v.name ?? 'vendor'} in a new tab`}
                        >
                          <ArrowUpRight className="size-4" />
                        </a>
                      )}
                    </div>
                    {detail && (
                      <p className="text-xs leading-normal text-muted-foreground">
                        {detail}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default SolutionFamilyDetailModal
